/* Admin → Rebates / Incentives
   PDF-first monthly rebate workflow:
     1. Upload the GM/Chevrolet monthly rebate PDF (preview only by default).
     2. Review parsed offers — matched vehicles, warnings, missing expirations.
     3. Approve/edit/delete, then Publish (Replace all active OR Add to existing).
   Plus a full manual offer editor as backup. Offers are display-only on New
   vehicle pages and never subtracted from the advertised price. */
import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";

const OFFER_TYPES = [
  ["customer_cash", "Customer Cash"], ["bonus_cash", "Bonus Cash"],
  ["lease_cash", "Lease Cash"], ["finance_apr", "Finance APR Offer"],
  ["loyalty", "Loyalty"], ["conquest", "Conquest"], ["military", "Military"],
  ["first_responder", "First Responder"], ["college", "College"],
  ["supplier_employee", "Supplier / Employee"], ["ev_offer", "EV-Specific"],
  ["other", "Other"],
];

const EV_RULES = [
  ["include", "Include EVs"], ["exclude", "Exclude EVs"], ["ev_only", "EV only"],
];

const EMPTY_OFFER = {
  name: "", offer_type: "bonus_cash", amount: "", apr_text: "", lease_text: "",
  years: "", make: "Chevrolet", model: "", trim: "", category: "",
  ev_rule: "include", qualification_required: false, included_in_price: false,
  manually_applied: false, start_date: "", end_date: "", disclaimer: "",
  active: true,
};

const STATUS_BADGE = {
  active: "bg-emerald-100 text-emerald-700",
  draft: "bg-amber-100 text-amber-700",
  inactive: "bg-slate-200 text-slate-600",
  expired: "bg-red-100 text-red-700",
};

function fmtAmt(o) {
  if (o.amount) return `$${Number(o.amount).toLocaleString()}`;
  return o.apr_text || o.lease_text || "—";
}

export default function AdminRebates() {
  const [tab, setTab] = useState("offers");
  const [offers, setOffers] = useState([]);
  const [vehicleCount, setVehicleCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");  // "" | "cash" | "apr" | "ev" | "no_ev" | "truck" | "car_suv"
  const [bulkSelected, setBulkSelected] = useState({}); // {id: true}
  const [uploads, setUploads] = useState([]);
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null); // offer object or "new"
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // PDF upload review state
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [review, setReview] = useState(null); // {upload, offers, warnings, review}
  const [publishMode, setPublishMode] = useState("preview");
  const [selectedIds, setSelectedIds] = useState({});
  const [publishResult, setPublishResult] = useState(null);

  const loadOffers = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/rebates/offers", {
        params: statusFilter ? { status: statusFilter } : {},
      });
      setOffers(data.items || []);
      setVehicleCount(data.new_vehicle_count || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load offers.");
    }
  }, [statusFilter]);

  const loadHistory = useCallback(async () => {
    try {
      const [u, ev] = await Promise.all([
        api.get("/admin/rebates/uploads"),
        api.get("/admin/rebates/events"),
      ]);
      setUploads(u.data.items || []);
      setEvents(ev.data.items || []);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { loadOffers(); }, [loadOffers]);
  useEffect(() => { if (tab === "history") loadHistory(); }, [tab, loadHistory]);

  // Phase: derived filtering by type/category for the offers tab.
  const TRUCK_MODELS = ["Silverado", "Colorado", "BrightDrop"];
  const CAR_SUV_MODELS = ["Trax", "Equinox", "Blazer", "Traverse", "Tahoe", "Suburban", "Malibu", "Corvette", "Camaro", "Trailblazer", "Bolt"];
  const offerMatchesType = (o) => {
    const t = (typeFilter || "").toLowerCase();
    if (!t) return true;
    const otype = (o.offer_type || "").toLowerCase();
    const ev = (o.ev_rule || "include").toLowerCase();
    const model = (o.model || "").toLowerCase();
    if (t === "cash") return otype.includes("cash") || otype.includes("rebate");
    if (t === "apr") return otype.includes("apr") || otype.includes("finance");
    if (t === "ev") return ev === "ev_only" || (o.offer_type || "").toLowerCase().includes("ev");
    if (t === "no_ev") return ev === "no_ev" || ev === "exclude";
    if (t === "truck") return TRUCK_MODELS.some((m) => model.includes(m.toLowerCase()));
    if (t === "car_suv") return CAR_SUV_MODELS.some((m) => model.includes(m.toLowerCase()));
    return true;
  };
  const filteredOffers = offers.filter(offerMatchesType);

  const allFilteredSelected = filteredOffers.length > 0 && filteredOffers.every((o) => bulkSelected[o.id]);
  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setBulkSelected({});
    } else {
      const next = { ...bulkSelected };
      filteredOffers.forEach((o) => { next[o.id] = true; });
      setBulkSelected(next);
    }
  };
  const toggleOne = (id) => setBulkSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectedCount = Object.values(bulkSelected).filter(Boolean).length;

  const bulkActivate = async (activate) => {
    const ids = filteredOffers.filter((o) => bulkSelected[o.id]).map((o) => o.id);
    if (!ids.length) return;
    if (!window.confirm(`${activate ? "Activate" : "Deactivate"} ${ids.length} offer${ids.length === 1 ? "" : "s"}?`)) return;
    setBusy(true);
    setError("");
    try {
      // Sequential to keep audit clean; PUT updates one at a time.
      for (const id of ids) {
        const offer = offers.find((o) => o.id === id);
        if (!offer) continue;
        await api.put(`/admin/rebates/offers/${id}`, { ...offer, years: (offer.years || []).join(","), active: !!activate });
      }
      setBulkSelected({});
      await loadOffers();
    } catch (e) {
      setError(e?.response?.data?.detail || "Bulk update failed.");
    } finally {
      setBusy(false);
    }
  };

  async function uploadPdf() {
    if (!pdfFile) return;
    setUploading(true);
    setError("");
    setReview(null);
    setPublishResult(null);
    try {
      const fd = new FormData();
      fd.append("file", pdfFile);
      const { data } = await api.post("/admin/rebates/upload-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 180000,
      });
      setReview(data);
      const sel = {};
      (data.offers || []).forEach((o) => { sel[o.id] = !!o.end_date; });
      setSelectedIds(sel);
      setPublishMode("preview");
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  async function publish() {
    if (!review || publishMode === "preview") return;
    setBusy(true);
    setError("");
    try {
      const offer_ids = Object.entries(selectedIds).filter(([, v]) => v).map(([k]) => k);
      const { data } = await api.post(`/admin/rebates/uploads/${review.upload.id}/publish`, {
        mode: publishMode, offer_ids,
      });
      setPublishResult(data);
      await loadOffers();
    } catch (e) {
      setError(e?.response?.data?.detail || "Publish failed.");
    } finally {
      setBusy(false);
    }
  }

  async function saveOffer(form) {
    setBusy(true);
    setError("");
    const payload = {
      ...form,
      amount: form.amount === "" ? null : Number(form.amount),
      years: String(form.years || "").split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (form.id) await api.put(`/admin/rebates/offers/${form.id}`, payload);
      else await api.post("/admin/rebates/offers", payload);
      setEditing(null);
      await loadOffers();
      if (review) {
        // refresh review offers if editing a draft from the review screen
        const { data } = await api.get("/admin/rebates/offers", { params: { status: "draft" } });
        setReview((r) => r ? { ...r, offers: (data.items || []).filter((o) => o.upload_id === r.upload.id) } : r);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  }

  async function removeOffer(id) {
    if (!window.confirm("Delete this offer?")) return;
    try {
      await api.delete(`/admin/rebates/offers/${id}`);
      await loadOffers();
      if (review) setReview((r) => r ? { ...r, offers: r.offers.filter((o) => o.id !== id) } : r);
    } catch (e) {
      setError(e?.response?.data?.detail || "Delete failed.");
    }
  }

  async function toggleActive(o) {
    try {
      await api.put(`/admin/rebates/offers/${o.id}`, { ...o, years: (o.years || []).join(","), active: o.status !== "active" });
      await loadOffers();
    } catch (e) {
      setError(e?.response?.data?.detail || "Update failed.");
    }
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl" data-testid="admin-rebates-page">
      <div className="rounded-2xl bg-[#061426] text-white p-5 mb-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-blue-300 font-bold">Growth</div>
        <h1 className="text-2xl font-extrabold mt-0.5">Rebates &amp; Incentives</h1>
        <p className="text-sm text-white/60 mt-1">
          Upload the monthly rebate PDF, review parsed offers, then publish. Offers display on
          New vehicle pages only and are <b>never subtracted</b> from the advertised price
          (vAuto already bakes rebates into Price).
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm font-semibold" data-testid="rebates-error">
          {error}
        </div>
      )}

      <div className="flex gap-2 mb-5">
        {[["offers", "Offers"], ["upload", "Upload Monthly PDF"], ["history", "History"]].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={`px-4 py-2 rounded-full text-sm font-bold border ${tab === k ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-300 hover:border-blue-400"}`}
            data-testid={`rebates-tab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {/* ---------------- OFFERS TAB ---------------- */}
      {tab === "offers" && (
        <div data-testid="rebates-offers-tab">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="rebates-status-filter">
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="inactive">Inactive</option>
              <option value="expired">Expired</option>
            </select>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="rebates-type-filter">
              <option value="">All types</option>
              <option value="cash">Cash rebates only</option>
              <option value="apr">APR / Finance only</option>
              <option value="ev">EV vehicles only</option>
              <option value="no_ev">Non-EV (gas) only</option>
              <option value="truck">Trucks (Silverado / Colorado / BrightDrop)</option>
              <option value="car_suv">Cars &amp; SUVs (Trax / Equinox / Blazer / Tahoe / etc.)</option>
            </select>
            <div className="text-xs text-slate-500">
              {vehicleCount} live New vehicles to match against · Showing {filteredOffers.length}{offers.length !== filteredOffers.length ? ` of ${offers.length}` : ""}
            </div>
            <button type="button" onClick={() => setEditing({ ...EMPTY_OFFER })}
              className="ml-auto px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700"
              data-testid="rebates-add-offer">
              + Add Offer Manually
            </button>
          </div>

          {/* Quick mass actions on currently-filtered set */}
          {filteredOffers.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-3 p-2 rounded-lg bg-slate-50 border border-slate-200" data-testid="rebates-quick-actions">
              <span className="text-xs font-bold text-slate-700 mr-1">Quick actions on filtered ({filteredOffers.length}):</span>
              <button type="button" onClick={async () => {
                if (!window.confirm(`Activate all ${filteredOffers.length} filtered offer(s)?`)) return;
                setBusy(true); setError("");
                try {
                  for (const o of filteredOffers) {
                    await api.put(`/admin/rebates/offers/${o.id}`, { ...o, years: (o.years || []).join(","), active: true });
                  }
                  await loadOffers();
                } catch (e) { setError(e?.response?.data?.detail || "Bulk activate failed."); }
                finally { setBusy(false); }
              }} disabled={busy}
                className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-50"
                data-testid="rebates-activate-all-filtered">
                Activate ALL filtered
              </button>
              <button type="button" onClick={async () => {
                if (!window.confirm(`Deactivate all ${filteredOffers.length} filtered offer(s)?`)) return;
                setBusy(true); setError("");
                try {
                  for (const o of filteredOffers) {
                    await api.put(`/admin/rebates/offers/${o.id}`, { ...o, years: (o.years || []).join(","), active: false });
                  }
                  await loadOffers();
                } catch (e) { setError(e?.response?.data?.detail || "Bulk deactivate failed."); }
                finally { setBusy(false); }
              }} disabled={busy}
                className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-bold hover:bg-amber-700 disabled:opacity-50"
                data-testid="rebates-deactivate-all-filtered">
                Deactivate ALL filtered
              </button>
              <span className="text-[10px] text-slate-500 ml-2">Tip: change the type filter (e.g. "Cash rebates only") to scope the action.</span>
            </div>
          )}

          {/* Bulk action bar */}
          {selectedCount > 0 && (
            <div className="flex flex-wrap items-center gap-3 mb-3 p-3 rounded-lg bg-blue-50 border border-blue-200" data-testid="rebates-bulk-bar">
              <span className="text-sm font-bold text-blue-900">{selectedCount} selected</span>
              <button type="button" onClick={() => bulkActivate(true)} disabled={busy}
                className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-50"
                data-testid="rebates-bulk-activate">
                Activate all selected
              </button>
              <button type="button" onClick={() => bulkActivate(false)} disabled={busy}
                className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-bold hover:bg-amber-700 disabled:opacity-50"
                data-testid="rebates-bulk-deactivate">
                Deactivate all selected
              </button>
              <button type="button" onClick={() => setBulkSelected({})}
                className="px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-slate-700 text-xs font-bold hover:bg-slate-50"
                data-testid="rebates-bulk-clear">
                Clear selection
              </button>
            </div>
          )}

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b">
                  <th className="px-3 py-2 w-10">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleSelectAll}
                      title={allFilteredSelected ? "Deselect all" : "Select all visible"}
                      data-testid="rebates-select-all"
                    />
                  </th>
                  <th className="px-3 py-2">Offer</th>
                  <th className="px-3 py-2">Value</th>
                  <th className="px-3 py-2">Applies To</th>
                  <th className="px-3 py-2">Label</th>
                  <th className="px-3 py-2">Window</th>
                  <th className="px-3 py-2">Matches</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOffers.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400" data-testid="rebates-empty">
                    {offers.length === 0 ? "No offers yet. Upload a monthly PDF or add one manually." : "No offers match the current filters."}
                  </td></tr>
                )}
                {filteredOffers.map((o) => (
                  <tr key={o.id} className={`border-b last:border-0 hover:bg-slate-50 ${bulkSelected[o.id] ? "bg-blue-50/40" : ""}`} data-testid={`rebate-offer-row-${o.id}`}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={!!bulkSelected[o.id]}
                        onChange={() => toggleOne(o.id)}
                        data-testid={`rebates-select-${o.id}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-bold text-slate-800">{o.name}
                      <div className="text-[10px] text-slate-400 font-normal">{(o.offer_type || "").replace(/_/g, " ")} · {o.source}</div>
                    </td>
                    <td className="px-3 py-2 font-bold tabular-nums">{fmtAmt(o)}</td>
                    <td className="px-3 py-2 text-slate-600">
                      {[o.years?.join("/"), o.make, o.model || "All models", o.trim].filter(Boolean).join(" ")}
                      {o.ev_rule !== "include" && <span className="ml-1 text-[10px] font-bold text-violet-600">[{o.ev_rule === "ev_only" ? "EV only" : "no EV"}]</span>}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-slate-500">{o.public_label}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {o.start_date || "—"} → <b className={!o.end_date ? "text-red-600" : ""}>{o.end_date || "MISSING"}</b>
                    </td>
                    <td className="px-3 py-2 tabular-nums font-bold">{o.matched_count ?? "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase ${STATUS_BADGE[o.status] || STATUS_BADGE.inactive}`}>
                        {o.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <button type="button" onClick={() => setEditing({ ...EMPTY_OFFER, ...o, amount: o.amount ?? "", years: (o.years || []).join(","), active: o.status === "active" })}
                        className="text-blue-600 font-bold text-xs mr-2 hover:underline" data-testid={`rebate-edit-${o.id}`}>Edit</button>
                      <button type="button" onClick={() => toggleActive(o)}
                        className="text-amber-600 font-bold text-xs mr-2 hover:underline">{o.status === "active" ? "Deactivate" : "Activate"}</button>
                      <button type="button" onClick={() => removeOffer(o.id)}
                        className="text-red-600 font-bold text-xs hover:underline" data-testid={`rebate-delete-${o.id}`}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---------------- UPLOAD TAB ---------------- */}
      {tab === "upload" && (
        <div data-testid="rebates-upload-tab">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="font-extrabold text-slate-800">Upload Monthly Rebate PDF</h2>
            <p className="text-xs text-slate-500 mt-1">
              The PDF is parsed into draft offers for review. <b>Nothing is published automatically</b> —
              you approve, edit, or delete offers below, then choose Replace or Add when publishing.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <input type="file" accept=".pdf" onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                className="text-sm" data-testid="rebates-pdf-input" />
              <button type="button" onClick={uploadPdf} disabled={!pdfFile || uploading}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold disabled:opacity-50 hover:bg-blue-700"
                data-testid="rebates-pdf-upload-btn">
                {uploading ? "Parsing PDF…" : "Upload & Parse (Preview Only)"}
              </button>
            </div>
          </div>

          {review && (
            <div className="mt-5 rounded-xl border border-blue-200 bg-blue-50/40 p-5" data-testid="rebates-review">
              <h3 className="font-extrabold text-slate-800">
                Review: {review.upload.filename}
                <span className="ml-2 text-sm font-semibold text-slate-500">({review.offers.length} offers parsed)</span>
              </h3>

              {review.warnings?.length > 0 && (
                <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid="rebates-review-warnings">
                  <b>Parsing warnings:</b>
                  <ul className="list-disc ml-4 mt-1 space-y-0.5">
                    {review.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                <Tile label="Offers Parsed" value={review.offers.length} />
                <Tile label="New Vehicles" value={review.review?.new_vehicle_count ?? 0} />
                <Tile label="No Match" value={review.review?.offers_with_no_match?.length ?? 0} warn={(review.review?.offers_with_no_match?.length ?? 0) > 0} />
                <Tile label="Missing Expiration" value={review.review?.offers_missing_expiration?.length ?? 0} warn={(review.review?.offers_missing_expiration?.length ?? 0) > 0} />
              </div>

              <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200 bg-white">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b">
                      <th className="px-2 py-2">Publish?</th>
                      <th className="px-2 py-2">Offer</th>
                      <th className="px-2 py-2">Value</th>
                      <th className="px-2 py-2">Applies To</th>
                      <th className="px-2 py-2">Expires</th>
                      <th className="px-2 py-2">Matched</th>
                      <th className="px-2 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {review.offers.map((o) => {
                      const m = review.review?.match_by_offer?.[o.id];
                      return (
                        <tr key={o.id} className="border-b last:border-0" data-testid={`review-offer-${o.id}`}>
                          <td className="px-2 py-2">
                            <input type="checkbox" checked={!!selectedIds[o.id]} disabled={!o.end_date}
                              onChange={(e) => setSelectedIds((s) => ({ ...s, [o.id]: e.target.checked }))} />
                          </td>
                          <td className="px-2 py-2 font-bold">{o.name}
                            <div className="text-[10px] text-slate-400 font-normal">{(o.offer_type || "").replace(/_/g, " ")}</div>
                          </td>
                          <td className="px-2 py-2 tabular-nums font-bold">{fmtAmt(o)}</td>
                          <td className="px-2 py-2 text-xs text-slate-600">{[o.years?.join("/"), o.make, o.model || "All models", o.trim].filter(Boolean).join(" ")}</td>
                          <td className={`px-2 py-2 text-xs font-bold ${!o.end_date ? "text-red-600" : "text-slate-700"}`}>{o.end_date || "MISSING — edit to set"}</td>
                          <td className="px-2 py-2 tabular-nums">{m ? m.matched_count : "—"}</td>
                          <td className="px-2 py-2 whitespace-nowrap">
                            <button type="button" onClick={() => setEditing({ ...EMPTY_OFFER, ...o, amount: o.amount ?? "", years: (o.years || []).join(","), active: false })}
                              className="text-blue-600 font-bold text-xs mr-2 hover:underline">Edit</button>
                            <button type="button" onClick={() => removeOffer(o.id)}
                              className="text-red-600 font-bold text-xs hover:underline">Delete</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-3 text-sm font-semibold text-slate-700">
                  {[["preview", "Preview only (default)"], ["replace", "Replace all active offers"], ["add", "Add to existing offers"]].map(([k, label]) => (
                    <label key={k} className="inline-flex items-center gap-1.5">
                      <input type="radio" name="pubmode" value={k} checked={publishMode === k}
                        onChange={() => setPublishMode(k)} data-testid={`rebates-mode-${k}`} />
                      {label}
                    </label>
                  ))}
                </div>
                <button type="button" onClick={publish} disabled={publishMode === "preview" || busy}
                  className="ml-auto px-5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-bold disabled:opacity-40 hover:bg-emerald-700"
                  data-testid="rebates-publish-btn">
                  {busy ? "Publishing…" : "Publish Selected Offers"}
                </button>
              </div>

              {publishResult && (
                <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800" data-testid="rebates-publish-result">
                  Published <b>{publishResult.published}</b> offers
                  {publishResult.replaced ? <> · deactivated <b>{publishResult.replaced}</b> previous offers</> : null}
                  {publishResult.skipped_missing_expiration?.length ? <> · skipped (no expiration): {publishResult.skipped_missing_expiration.join(", ")}</> : null}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ---------------- HISTORY TAB ---------------- */}
      {tab === "history" && (
        <div className="grid md:grid-cols-2 gap-5" data-testid="rebates-history-tab">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h3 className="font-extrabold text-slate-800 mb-3">PDF Uploads</h3>
            {uploads.length === 0 && <div className="text-sm text-slate-400">No uploads yet.</div>}
            {uploads.map((u) => (
              <div key={u.id} className="border-b last:border-0 py-2 text-sm">
                <div className="font-bold text-slate-700">{u.filename}</div>
                <div className="text-xs text-slate-500">
                  {u.uploaded_at?.slice(0, 16).replace("T", " ")} · {u.offers_parsed} offers ·{" "}
                  <span className={`font-bold ${u.status === "published" ? "text-emerald-600" : "text-amber-600"}`}>{u.status}</span>
                  {u.published_mode ? ` (${u.published_mode}, ${u.published_count})` : ""}
                </div>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h3 className="font-extrabold text-slate-800 mb-3">Recent Changes</h3>
            {events.length === 0 && <div className="text-sm text-slate-400">No events yet.</div>}
            {events.map((e) => (
              <div key={e.id} className="border-b last:border-0 py-2 text-xs">
                <span className="font-extrabold uppercase text-[10px] text-blue-600">{(e.type || "").replace(/_/g, " ")}</span>{" "}
                <span className="font-bold text-slate-700">{e.offer_name}</span>{" "}
                <span className="text-slate-500">{e.detail}</span>
                <div className="text-[10px] text-slate-400">{e.at?.slice(0, 16).replace("T", " ")}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---------------- OFFER FORM MODAL ---------------- */}
      {editing && (
        <OfferForm
          initial={editing}
          busy={busy}
          onCancel={() => setEditing(null)}
          onSave={saveOffer}
        />
      )}
    </div>
  );
}

function Tile({ label, value, warn }) {
  return (
    <div className={`rounded-lg border p-2.5 ${warn ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">{label}</div>
      <div className={`text-xl font-black ${warn ? "text-amber-700" : "text-slate-800"}`}>{value}</div>
    </div>
  );
}

function OfferForm({ initial, busy, onCancel, onSave }) {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="rebate-offer-form">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 my-8">
        <h3 className="font-extrabold text-lg text-slate-800">{f.id ? "Edit Offer" : "Add Offer Manually"}</h3>
        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <label className="col-span-2">
            <span className="text-xs font-bold text-slate-500">Offer Name *</span>
            <input className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2" value={f.name}
              onChange={(e) => set("name", e.target.value)} data-testid="offer-form-name" />
          </label>
          <label>
            <span className="text-xs font-bold text-slate-500">Offer Type</span>
            <select className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2" value={f.offer_type}
              onChange={(e) => set("offer_type", e.target.value)}>
              {OFFER_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
          <label>
            <span className="text-xs font-bold text-slate-500">Amount ($)</span>
            <input className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2" inputMode="numeric"
              value={f.amount} onChange={(e) => set("amount", e.target.value)} data-testid="offer-form-amount" />
          </label>
          <label>
            <span className="text-xs font-bold text-slate-500">APR Offer Text</span>
            <input className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2" value={f.apr_text}
              onChange={(e) => set("apr_text", e.target.value)} placeholder="e.g. 1.9% APR for 60 mo" />
          </label>
          <label>
            <span className="text-xs font-bold text-slate-500">Lease Offer Text</span>
            <input className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2" value={f.lease_text}
              onChange={(e) => set("lease_text", e.target.value)} placeholder="e.g. $299/mo 36 mo" />
          </label>
          <label>
            <span className="text-xs font-bold text-slate-500">Make</span>
            <input className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2" value={f.make}
              onChange={(e) => set("make", e.target.value)} />
          </label>
          <label>
            <span className="text-xs font-bold text-slate-500">Model (blank = all models)</span>
            <input className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2" value={f.model}
              onChange={(e) => set("model", e.target.value)} placeholder="Equinox" data-testid="offer-form-model" />
          </label>
          <label>
            <span className="text-xs font-bold text-slate-500">Year(s), comma-separated</span>
            <input className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2" value={f.years}
              onChange={(e) => set("years", e.target.value)} placeholder="2026" />
          </label>
          <label>
            <span className="text-xs font-bold text-slate-500">Trim (optional)</span>
            <input className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2" value={f.trim}
              onChange={(e) => set("trim", e.target.value)} />
          </label>
          <label>
            <span className="text-xs font-bold text-slate-500">Category (optional)</span>
            <select className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2" value={f.category}
              onChange={(e) => set("category", e.target.value)}>
              <option value="">Any</option>
              <option value="car">Car</option>
              <option value="suv">SUV</option>
              <option value="truck">Truck</option>
              <option value="van">Van</option>
            </select>
          </label>
          <label>
            <span className="text-xs font-bold text-slate-500">EV Rule</span>
            <select className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2" value={f.ev_rule}
              onChange={(e) => set("ev_rule", e.target.value)}>
              {EV_RULES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
          <label>
            <span className="text-xs font-bold text-slate-500">Start Date</span>
            <input type="date" className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2" value={f.start_date}
              onChange={(e) => set("start_date", e.target.value)} />
          </label>
          <label>
            <span className="text-xs font-bold text-slate-500">End Date (required) *</span>
            <input type="date" className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2" value={f.end_date}
              onChange={(e) => set("end_date", e.target.value)} data-testid="offer-form-end-date" />
          </label>
          <label className="col-span-2">
            <span className="text-xs font-bold text-slate-500">Public Disclaimer Text</span>
            <textarea className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2" rows={2} value={f.disclaimer}
              onChange={(e) => set("disclaimer", e.target.value)} />
          </label>
          <div className="col-span-2 grid grid-cols-2 gap-2 text-xs font-semibold text-slate-700">
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={f.included_in_price} onChange={(e) => set("included_in_price", e.target.checked)} />
              Included in advertised price
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={f.qualification_required} onChange={(e) => set("qualification_required", e.target.checked)} />
              Qualification required
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={f.manually_applied} onChange={(e) => set("manually_applied", e.target.checked)} />
              Manually applied
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} data-testid="offer-form-active" />
              Active (publicly visible)
            </label>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-bold text-slate-600">Cancel</button>
          <button type="button" onClick={() => onSave(f)} disabled={busy || !f.name || (f.active && !f.end_date)}
            className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold disabled:opacity-40 hover:bg-blue-700"
            data-testid="offer-form-save">
            {busy ? "Saving…" : "Save Offer"}
          </button>
        </div>
        {f.active && !f.end_date && (
          <p className="mt-2 text-right text-[11px] text-red-600 font-bold">Active offers require an end date.</p>
        )}
      </div>
    </div>
  );
}
